// ----------------------------------------------------------------------
// <copyright file="Izenda.Izenda.ReportPartGridProcessData.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartGridProcessData
 * Utility for processing data for Pivot/DrillDown grid
 */

import processDataUtils from 'share/utils/Izenda.ProcessDataUtils';
import ColumnDataObject from './models/Izenda.ColumnDataObject';
import RowDataObject from './models/Izenda.RowDataObject';
import { CUSTOM_FORMAT_PREFIX } from 'logic/data/Izenda.ReportPartFieldConstants';
import { toJS } from 'mobx';
import {
  at,
  clone,
  concat,
  filter,
  find,
  flatMap,
  flow,
  forEach,
  fromPairs,
  get,
  head,
  includes,
  isArrayLike,
  isEmpty,
  isNil,
  map,
  mapValues,
  partial,
  placeholder,
  reduce,
  reject,
  remove,
  set,
  sortBy,
  toString
} from 'lodash/fp';

/**
 * Generate data object for row
 * @param  {array} records
 * @param  {array} previewData
 * @param  {array} rowsContainer
 * @return {array} row data object
 */
const createRowDataObject = (records, previewData, rowsContainer, keyPath, forDrillDown, isExpand) => {
  const filterMappingValue = processDataUtils.filterMappingByContainer(previewData.fieldsMapping, rowsContainer);
  const multiLevelData = processDataUtils.splitDataForSeparator(records, filterMappingValue, true, false, forDrillDown);

  return map(
    RowDataObject.createRowDataObject.bind(null, rowsContainer.elements.length, keyPath, isExpand),
    multiLevelData
  );
};

/**
 * Generate data object headers for row
 * @param  {object} rowsContainer
 * @param  {object} columnsContainer
 * @return {array} row header data object
 */
const createRowHeadersDataObject = rowsContainer => {
  const leafRowHeaders = map(
    item =>
      ColumnDataObject.createLeafNode(
        1,
        null,
        '',
        ColumnDataObject.COLUMN_TYPE.ROW,
        {
          element: item
        },
        true
      ),
    rowsContainer.visibleElement
  );

  return leafRowHeaders;
};

/**
 * Filter Mapping By Container
 * @param  {Array} fieldsMapping
 * @param  {Object} container
 * @return {Array}
 */
const filterMappingByContainer = function(fieldsMapping, container) {
  const filterMapping = processDataUtils.filterMappingByContainer(fieldsMapping, container);
  return filter(item => !!get('element.properties.fieldItemVisible', item), filterMapping);
};

/**
 * Generate data object headers for column
 * @param  {array} records Raw data
 * @param  {object} previewData
 * @param  {object} columnsContainer
 * @param  {object} valueContainer
 * @return {array} column header data object
 */
const createColumnDataObject = (
  records,
  previewData,
  columnsContainer,
  valueContainer,
  separatorContainer,
  keyPath
) => {
  const filterMappingColumn = processDataUtils.filterMappingByContainer(previewData.fieldsMapping, columnsContainer);
  const filterMappingValue = filterMappingByContainer(previewData.fieldsMapping, valueContainer);
  let maxLevel;
  let sideTotalsMapping;
  let result = [];
  let multiLevelData = [];

  if (isEmpty(filterMappingColumn)) {
    // Process Data Column for DrillDown
    result = map(
      ColumnDataObject.createLeafNode.bind(null, 1, null, '', ColumnDataObject.COLUMN_TYPE.VALUE),
      filterMappingValue
    );
  } else {
    // Process Data Column for Pivot
    maxLevel = filterMappingColumn.length + 1;

    // generate siteTotalsMapping
    sideTotalsMapping = sortBy(
      'position',
      reduce(
        (mapping, item) => {
          const valueElement = find(['field.fieldNameAlias', item.fieldNameAlias], valueContainer.visibleElement);
          if (valueElement) {
            const newItem = clone(item);
            newItem.element = valueElement;
            mapping.push(newItem);
          }

          return mapping;
        },
        [],
        previewData.sideTotalMapping
      )
    );

    var recordList = records.data || records;
    multiLevelData = processDataUtils.splitDataForSeparator(recordList.slice(), filterMappingColumn, true, true);

    result = map(
      ColumnDataObject.createBranchNode.bind(null, filterMappingValue, maxLevel, sideTotalsMapping, []),
      multiLevelData
    );
  }
  return result;
};

const flattenRowDataObject = (rowDataObject, forDrillDown, haveSuptotal, isExpandedRowHasSpacing) => {
  if (rowDataObject.ignore)
    return rowDataObject;
  const partialFunc = partial(flattenRowDataObject, [placeholder, forDrillDown, haveSuptotal, isExpandedRowHasSpacing]);

  if (isArrayLike(rowDataObject)) {
    return flatMap(partialFunc, rowDataObject);
  }
  const currentRow = {
    rowDataObject,
    firstRow: true
  };

  if ((rowDataObject.isExpand || (rowDataObject.element && !rowDataObject.element.isVisible)) && !isEmpty(rowDataObject.childs)) {
    let childDataRecords = flatMap(partialFunc, rowDataObject.childs);

    // Add expandable row spacing
    const isExpandSpacingRequired = rowDataObject.element.isVisible &&
      (forDrillDown || isExpandedRowHasSpacing);
    if (isExpandSpacingRequired) {
      childDataRecords = concat([{ currentRow, rowDataObjects: {} }], childDataRecords);
    }

    // Add subtotal
    if ((forDrillDown && rowDataObject.element.isVisible)) {
      if (haveSuptotal) {
        childDataRecords = concat(childDataRecords, [
          { currentRow: set('isSupTotalRow', true, set('firstRow', false, currentRow)), rowDataObjects: {} }
        ]);
      }
    }

    const lastRowIndex = childDataRecords.length - 1;
    childDataRecords.map((item, index) => {
      item.rowDataObjects[rowDataObject.uniqueFieldNameAlias] = {
        rowDataObject,
        firstRow: index === 0,
        isSupTotalRow: index === lastRowIndex && forDrillDown && haveSuptotal && rowDataObject.size > 1
      };
    });
    return childDataRecords;
  }
  // last group
  const returnRow = {
    currentRow,
    rowDataObjects: {}
  };

  returnRow.currentRow.isSupTotalRow =
    forDrillDown && haveSuptotal && !isEmpty(rowDataObject.childs) && !rowDataObject.isExpand;

  returnRow.rowDataObjects[rowDataObject.uniqueFieldNameAlias] = currentRow;
  return [returnRow];
};

const updateSideTotalMapping = (sideTotalColumnDataObject, sideTotalMapping, columnsContainer) => {
  if (!isEmpty(sideTotalMapping)) {
    const columnFieldNameAlias = map(get('field.fieldNameAlias'), columnsContainer.visibleElement);
    const sideTotalsMapping = reject(item => includes(item.groupFieldLevel, columnFieldNameAlias), sideTotalMapping);

    forEach(item => {
      const mapping = find(
        {
          fieldNameAlias: item.element.field.fieldNameAlias
        },
        sideTotalsMapping
      );

      mapping && !includes(mapping.columnName, item.keyList) && item.keyList.unshift(mapping.columnName);
    }, sideTotalColumnDataObject);
  }
};
/**
 * create subtotals headers
 * @param  {array} columnDataObjects
 * @return {array} subtotal
 */
const createSideTotal = (previewData, valueContainer) => {
  const filterMappingValue = processDataUtils.filterMappingByContainer(previewData.fieldsMapping, valueContainer);
  const sideTotalColumns = map(
    ColumnDataObject.createLeafNode.bind(null, 1, null, 'Total', ColumnDataObject.COLUMN_TYPE.SIDE_TOTAL),
    filterMappingValue
  );

  return filter(item => {
    item.forcePrefix = true;
    return item.element.isVisible;
  }, sideTotalColumns);
};

/**
 * recursive function for generating blank row headers
 * @param  {int}           level          [current level]
 * @param  {int}           maxLevel       [max level can be insert]
 * @param  {array of ColumnDataObject}           leafRowHeaders [leaf ColumnDataObject]
 */
function createBlankHeaders(level, maxLevel, leafRowHeaders, isVisible) {
  const header = new ColumnDataObject.ColumnDataObject(true);
  const newLevel = level + 1;

  setHeaderValue(header);

  if (newLevel >= maxLevel) {
    header.childs = leafRowHeaders;
    return header;
  }

  function setHeaderValue(itemHeader) {
    itemHeader.isLeaf = false;
    itemHeader.isHidden = true;
    itemHeader.level = newLevel;
    itemHeader.isInvisible = !isVisible;
  }

  header.childs[0] = createBlankHeaders(newLevel, maxLevel, leafRowHeaders, isVisible);

  return header;
}

/**
 * create first row ('All' row) for DrillDown grid
 * @param  {String} fieldNameAlias
 * @param  {Array}  childs
 * @param  {Array}  keyPath
 * @param  {Number} level
 * @param  {Object} RowDataObject
 */
function createAllRowDataObject(fieldNameAlias, childs, keyPath, level) {
  const item = {
    key: [
      {
        columnName: '',
        value: 'All'
      }
    ],
    data: []
  };
  const rowDataObject = RowDataObject.createRowDataObject(-1, [], true, item);

  rowDataObject.childs = childs;
  rowDataObject.isExpand = true;
  rowDataObject.level = level;
  rowDataObject.ascendingLevel = -1;
  rowDataObject.subKeyPath = keyPath || [];
  rowDataObject.isAllRow = true;

  const currentRow = {
    rowDataObject,
    isSupTotalRow: false,
    firstRow: true,
    mustHaveExpander: true
  };
  // last group
  const returnRow = {
    currentRow,
    rowDataObjects: {}
  };

  returnRow.rowDataObjects[fieldNameAlias] = currentRow;
  return returnRow;
}
function createSupTotalRowDataObject(fieldNameAlias, childs, element, value, data) {
  const item = {
    key: [
      {
        columnName: '',
        element,
        value
      }
    ],
    data
  };
  const rowDataObject = RowDataObject.createRowDataObject(0, [], true, item);

  const currentRow = {
    rowDataObject,
    isSupTotalRow: true,
    firstRow: false
  };
  // last group
  const returnRow = {
    currentRow,
    rowDataObjects: {}
  };

  rowDataObject.childs = childs;
  rowDataObject.isExpand = true;
  returnRow.rowDataObjects[fieldNameAlias] = currentRow;
  return returnRow;
}

function buildRawRecordFilterForPivotGrid(columnData, records) {
  const data = flow(
    toJS,
    map(mapValues(toString))
  )(records);

  return filter(fromPairs(columnData.keyPath), data);
}

/**
 * Get cell data
 * @param  {Object} columnData
 * @param  {Object} rowData
 * @param  {Array}  rowExpanders
 * @param  {Object} forDrillDown
 * @param  {Array}  subTotalMapping
 * @return {Object} data object
 */
function getDataForCell(columnData, rowData, rowExpanders, forDrillDown, subTotalMapping) {
  const currentRow = rowData.currentRow;

  if (columnData.type === ColumnDataObject.COLUMN_TYPE.ROW && (!currentRow.isSupTotalRow || forDrillDown)) {
    const currentRowInfo = rowData.rowDataObjects[columnData.uniqueFieldNameAlias];

    return {
      isEmpty: !currentRowInfo || (rowExpanders && !currentRowInfo.isSupTotalRow && !currentRowInfo.firstRow),
      data: get('rowDataObject.value', currentRowInfo),
      isAllRow: get('rowDataObject.isAllRow', currentRowInfo)
    };
  } else if (!currentRow.isSupTotalRow) {
    var rowDataObject = currentRow.rowDataObject;
    const isEmptyRow = rowExpanders && rowDataObject.isExpand && !isEmpty(rowDataObject);
    const data = !isEmptyRow && getDataByKey(rowDataObject.data, fromPairs(columnData.keyPath), concat(columnData.keyList, columnData.key));
    const orgData = !isEmptyRow && getDataByKey(rowDataObject.data, fromPairs(columnData.keyPath), concat(`${CUSTOM_FORMAT_PREFIX}${columnData.keyList}`, `${CUSTOM_FORMAT_PREFIX}${columnData.key}`));

    return {
      isEmpty: isEmptyRow,
      dataNotFound: isNil(data),
      data: data || '',
      // if the original value is undefined leave as it is. no need to replace it with an empty string. in order to distinguish empty string and absence of data.
      orgData: orgData
    };
  }
  const searchCriteria = {
    fieldNameAlias: columnData.uniqueFieldNameAlias
  };
  rowDataObject = rowData.currentRow.rowDataObject;

  if (columnData.type !== ColumnDataObject.COLUMN_TYPE.ROW) {
    searchCriteria.groupFieldLevel = forDrillDown ? rowDataObject.uniqueFieldNameAlias : columnData.groupFieldLevel;
  }

  let datas = [];
  if (rowDataObject.isAllRow) {
    datas = rowDataObject.data;
    subTotalMapping = rowDataObject.subTotalMapping;
  } else {
    datas = rowDataObject.childsData;
  }

  // compare using undefined for groupFieldLevel
  const key = find(
    item =>
      item.fieldNameAlias === searchCriteria.fieldNameAlias &&
      (searchCriteria.groupFieldLevel === item.groupFieldLevel ||
        (!searchCriteria.groupFieldLevel && isEmpty(item.groupFieldLevel))),
    subTotalMapping
  );

  return {
    isEmpty: !key,
    data:
      key &&
      flow(
        find(fromPairs(columnData.keyPath)),
        get(key.columnName)
      )(datas)
  };
}

function getDataByKey(data, keyPath, keyList) {
  return flow(
      find(keyPath),
      at(keyList),
      remove(isNil),
      head
    )(data);
}

function getRawData(columnData, rowData) {
  let rawData;

  if (columnData.type === ColumnDataObject.COLUMN_TYPE.ROW) {
    rawData = get('rowDataObject.data', rowData.rowDataObjects[columnData.uniqueFieldNameAlias]);
  } else {
    rawData = get('currentRow.rowDataObject.data', rowData);
  }

  if(!rawData)
    return [];

  return buildRawRecordFilterForPivotGrid(columnData, rawData);
}

const getMultiLevelData = (records, previewData, rowsContainer) => {
  const filterMappingValue = processDataUtils.filterMappingByContainer(previewData.fieldsMapping, rowsContainer);
  return processDataUtils.splitDataForSeparator(records, filterMappingValue, true);
};

export default {
  buildRawRecordFilterForPivotGrid,
  createRowDataObject,
  createColumnDataObject,
  createRowHeadersDataObject,
  createBlankHeaders,
  createSideTotal,
  updateSideTotalMapping,
  flattenRowDataObject,
  createSupTotalRowDataObject,
  getDataForCell,
  createAllRowDataObject,
  getRawData,
  getMultiLevelData
};



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportPartGridProcessData.js